import React, { useEffect, useState, useRef } from "react";
import { useReportAutoRun } from "hooks/useReportAutoRun/useReportAutoRun";
import { useLocation } from "react-router-dom";

import { formatDate } from "helpers/Helpers";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH, TABLET_WIDTH } from "helpers/ScreenSizes";

import { GetReport, GetReportRun, GetReportStatus } from "api/rpc/2024-04/facilityAdmin/report/report";
import { GetVendor } from "api/rpc/2024-04/facilityAdmin/product/vendor";
import { GetRegisters } from "api/rpc/2024-04/facilityAdmin/facility/register/register";
import { GetFacilityAdmin } from "api/rpc/2024-04/facilityAdmin/client/admin/admin";
import { StatusCode } from "api/protocols";
import { IConfiguration } from "api/rpc/clientAdmin/client/report";
import { IFacilityStaff } from "redux/reducers/models/facility";
import { IRegister } from "../../Admin";

import Page from "components/page/Page";
import Card from "components/card/Card";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import FilterDropdown from "components/filterDropDown/FilterDropDown";
import SelectNew from "components/select/SelectNew";
import { useTranslation } from "react-i18next";

import "../report.scss";
import { useAppDispatch } from "hooks/redux";
import { closeInfo, showError, showInfo, showSuccess } from "redux/actions/ui";
import moment from "moment";
import ReportEmailModal from "components/reportEmailModal/ReportEmailModal";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import { DASHBOARD_COLOURS } from "../../dashboard/dashboardNew/dashboardWidgets/bookingType/BookingType";
import { delay } from "components/_util/delay";
import { ButtonNew } from "components/buttonNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave } from "@fortawesome/pro-regular-svg-icons";
import { formatReportCurrency } from "helpers/reportFormat";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface IReportState {
  id: number;
  title: string;
  code: string;
  dateRangeStart: Date;
  dateRangeEnd: Date;
  compareDateRangeStart: Date;
  compareDateRangeEnd: Date;
  singleDate: Date;
  configurations: Array<IConfiguration>;
  date_range: Array<string>;
  compare_to_date_range: Array<string>;
  date: string;
  facilityOptions: Array<{ label: string; onClick: () => void }>;
  facility_id_name: string;
  email: string;
  queue_after_days: number;
  startTime: string;
  endTime: string;
  allVendors: any;
  vendor_ids: Array<number>;
  allRegisters: IRegister[];
  register_ids: Array<number | null>;
  allUsers: IFacilityStaff[];
  user_ids: Array<number>;
  [key: string]: any;
}

interface IChartMetric {
  label: string;
  type: string;
}

interface IChartSeries {
  label: string;
  [metric: string]: string | number[];
}

interface IChartData {
  labels: string[];
  defaultMetric: string;
  metrics: Record<string, IChartMetric>;
  series: IChartSeries[];
}

interface IReportDataState {
  headings: any;
  rows: any;
  columns: Array<Record<string, any>>;
  tableDisplay: boolean;
  description: string;
  compileDate: string;
  chart: IChartData | null;
}

function formatChartValue(value: number, type: string): string {
  if (type === "currency") {
    return formatReportCurrency(value);
  }

  return new Intl.NumberFormat("en-CA").format(value);
}

export default function HourlySales() {
  const windowSize = useWindowSize();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const routeName = location.pathname.split("/");
  const code = routeName[routeName.length - 1];
  const { t } = useTranslation();
  const _APIPollFlag = useRef(false);
  const [pollCount, setPollCount] = useState<number>(0);
  const chartRef = useRef(null);
  const [selectedMetric, setSelectedMetric] = useState<string>("sales_total");

  function formatDateRange(startingDate: Date, endingDate: Date) {
    return [formatDate(startingDate), formatDate(endingDate) + " 23:59:59"];
  }

  const today = new Date();

  const [state, setState] = useState<IReportState>({
    dateRangeStart: today,
    dateRangeEnd: today,
    compareDateRangeStart: today,
    compareDateRangeEnd: today,
    singleDate: today,
    id: null,
    title: "",
    code: "",
    configurations: [],
    date_range: formatDateRange(today, today),
    compare_to_date_range: formatDateRange(today, today),
    date: formatDate(today),
    facilityOptions: [],
    facility_id_name: "",
    email: "",
    queue_after_days: 14,
    startTime: null,
    endTime: null,
    allVendors: null,
    vendor_ids: [],
    allRegisters: null,
    register_ids: [],
    allUsers: null,
    user_ids: [],
  });

  const [reportData, setReportData] = useState<IReportDataState>({
    headings: [],
    rows: [],
    columns: [],
    tableDisplay: false,
    description: "",
    compileDate: "",
    chart: null,
  });

  useEffect(() => {
    void loadReport();
    void getVendors();
    void getRegisters();
    void loadFacilityStaff();
  }, []);
  useReportAutoRun({
    isReady: state.id != null,
    reportId: state.id,
    setState,
    run: () => {
      void getReport(state.email);
    },
  });


  async function loadReport() {
    const reportRes = await GetReport({ code: code }, true);
    if (reportRes.status !== StatusCode.OK) {
      return;
    }

    const report = reportRes.data[0];
    const sortedConfigurations = [...report.configurations].sort((prev, next) => prev.position - next.position);

    setState(prev => ({
      ...prev,
      id: report.id,
      title: report.title,
      code: report.code,
      queue_after_days: report.queue_after_days,
      configurations: sortedConfigurations,
    }));
  }

  async function getVendors() {
    const vendorRes = await GetVendor(null, true);
    if (vendorRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.detailed_sales_by_department.003")));
      return;
    }
    setState(prevState => ({ ...prevState, allVendors: vendorRes.data }));
  }

  async function getRegisters() {
    const registerRes = await GetRegisters(null, true);
    if (registerRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.detailed_sales_by_department.004")));
      return;
    }
    setState(prevState => ({ ...prevState, allRegisters: registerRes.data }));
  }

  async function loadFacilityStaff() {
    const staffRes = await GetFacilityAdmin(null, true);
    if (staffRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading staff accounts")));
      return;
    }
    setState(prevState => ({ ...prevState, allUsers: staffRes.data }));
  }

  function onCheckBoxChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { value, checked, id } = e.target;
    let updateSelectedValues: any[] = [];
    if (state[id]) {
      updateSelectedValues = [...state[id]];
    }
    if (checked) {
      updateSelectedValues.push(value === "null" ? null : Number(value));
    } else {
      const valueToCompare = value === "null" ? null : Number(value);
      const removeIndex = updateSelectedValues?.findIndex(valueId => valueId === valueToCompare);
      if (removeIndex !== -1) {
        updateSelectedValues.splice(removeIndex, 1);
      }
    }
    setState(prevState => ({ ...prevState, [id]: updateSelectedValues }));
  }

  function handleRemoveAll(id: string) {
    setState(prevState => ({ ...prevState, [id]: [] }));
  }

  function handleAddAllVendors(id: string) {
    const updatedSelectedVendors: number[] = [];
    state.allVendors?.forEach((vendor: { id: number }) => {
      updatedSelectedVendors.push(vendor.id);
    });
    setState(prevState => ({
      ...prevState,
      [id]: updatedSelectedVendors,
    }));
  }

  function handleAddAllStaff(id: string) {
    const updatedSelectedUsers: number[] = [];
    state.allUsers?.forEach((user: { id: number }) => {
      updatedSelectedUsers.push(user.id);
    });
    setState(prevState => ({
      ...prevState,
      [id]: updatedSelectedUsers,
    }));
  }

  function handleAddAllRegisters(id: string) {
    const updatedSelectedRegisters: (number | null)[] = [null];
    state.allRegisters?.forEach((register: { id: number }) => {
      updatedSelectedRegisters.push(register.id);
    });
    setState(prevState => ({
      ...prevState,
      [id]: updatedSelectedRegisters,
    }));
  }

  async function getReport(email: string) {
    setReportData(prev => ({
      ...prev,
      rows: [],
      columns: [],
      description: null,
      chart: null,
    }));

    dispatch(showInfo("Loading report...", null, true, "Your report is being prepared. This may take a few minutes"));

    const reportRes = await GetReportRun(
      {
        report_id: state.id,
        date_range: state.date_range,
        compare_to_date_range: state.compare_to_date_range,
        should_queue: true,
        time_range: state.startTime === null || state.endTime === null ? undefined : [state.startTime, state.endTime],
        vendor_ids: state.vendor_ids,
        register_ids: state.register_ids,
        user_ids: state.user_ids,
        email,
      },
      false,
    );

    if (reportRes.status !== StatusCode.OK) {
      dispatch(closeInfo());
      dispatch(showError(reportRes.message));
      return;
    }

    _APIPollFlag.current = true;
    void pollReport(reportRes.data?.executed_report_id as number);

    return;
  }

  const pollReport = async (executed_report_id: number) => {
    if (executed_report_id) {
      const res = await GetReportStatus({ executed_report_id }, false);
      if (res.status === StatusCode.OK && res.data?.status === "completed") {
        setPollCount(0);
        _APIPollFlag.current = false;
        const data = JSON.parse(res.data.report_data as string);
        const compileDate = res.data.completed_at ? res.data.completed_at.concat("Z") : "";

        setReportData(prev => ({
          ...prev,
          rows: data.rows,
          columns: data.columns,
          description: data?.description ?? "",
          compileDate,
          chart: data.chart ?? null,
        }));

        if (data.chart?.defaultMetric) {
          setSelectedMetric(String(data.chart.defaultMetric));
        }

        dispatch(closeInfo());
      } else {
        if (_APIPollFlag.current === true) {
          setPollCount(prev => prev + 1);
          await delay(pollCount > 5 ? 5000 : 1000);
          await pollReport(executed_report_id);
        }
      }
    } else {
      _APIPollFlag.current = false;
    }
  };

  useEffect(() => {
    if (state.dateRangeStart && state.dateRangeEnd) {
      const date_range = formatDateRange(state.dateRangeStart, state.dateRangeEnd);
      setState(prevState => ({ ...prevState, date_range }));
    }
  }, [state.dateRangeStart, state.dateRangeEnd]);

  useEffect(() => {
    if (state.compareDateRangeStart && state.compareDateRangeEnd) {
      const date_range = formatDateRange(state.compareDateRangeStart, state.compareDateRangeEnd);
      setState(prevState => ({ ...prevState, compare_to_date_range: date_range }));
    }
  }, [state.compareDateRangeStart, state.compareDateRangeEnd]);

  function setDateRangeStart(date: Date) {
    setState(prevState => ({
      ...prevState,
      dateRangeStart: date,
    }));
  }

  function setDateRangeEnd(date: Date) {
    setState(prevState => ({
      ...prevState,
      dateRangeEnd: date,
    }));
  }

  function setCompareDateRangeStart(date: Date) {
    setState(prevState => ({
      ...prevState,
      compareDateRangeStart: date,
    }));
  }

  function setCompareDateRangeEnd(date: Date) {
    setState(prevState => ({
      ...prevState,
      compareDateRangeEnd: date,
    }));
  }

  const [reportEmail, setReportEmail] = useState({
    handler: null,
    open: false,
  });

  function handleReportEmailSelected(email: string) {
    setState({ ...state, email });
    setReportEmail({
      ...reportEmail,
      open: false,
    });
  }

  function handleReportEmailClose() {
    setReportEmail({ handler: null, open: false });
  }

  useEffect(() => {
    if (reportEmail.open === false && reportEmail.handler !== null) {
      reportEmail.handler(state.email);
    }
  }, [reportEmail.open]);

  const primaryAction = {
    content: t("secure.facility.reports.pages.rounds_by_customer_type.003"),
    action: () => {
      if (reportShouldQueue()) {
        dispatch(showSuccess(
          t('Potential large report detected.'),
          10000,
          t('An email is required to send the report to upon completion.'),
        ));

        setReportEmail({
          open: true,
          handler: getReport,
        });
      } else {
        void getReport(state.email);
      }
    },
  };

  function reportFilename(): string {
    return `${state.title}_${moment(new Date()).format("YYYY-MM-DD").toString()}`
      .replace(/ /g, "_")
      .toLowerCase();
  }

  function reportShouldQueue(): boolean {
    return moment(state.dateRangeEnd).diff(moment(state.dateRangeStart), 'days') >= state.queue_after_days;
  }

  function getMetricOptions() {
    return Object.entries(reportData.chart?.metrics ?? {}).map(([id, metric]) => ({
      id,
      label: metric.label,
    }));
  }

  function getLineChartData() {
    const chart = reportData.chart;
    if (!chart) {
      return { labels: [], datasets: [] };
    }

    return {
      labels: chart.labels,
      datasets: chart.series.map((series, index) => {
        const color = DASHBOARD_COLOURS[index % DASHBOARD_COLOURS.length];
        const metricData = series[selectedMetric];

        return {
          label: series.label,
          data: Array.isArray(metricData) ? metricData : [],
          borderColor: color,
          backgroundColor: color,
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 5,
        };
      }),
    };
  }

  const downloadChart = (ref: any) => {
    if (!ref.current) {
      dispatch(showError("Chart does not exist"));
      return;
    }

    const image = ref.current.toBase64Image();

    const link = document.createElement('a');
    link.href = image;
    link.download = reportFilename() + '.png';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const whiteBackgroundPlugin = {
    id: 'whiteBackground',
    beforeDraw: (chart: any) => {
      const { ctx } = chart;
      ctx.save();
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, chart.width, chart.height);
      ctx.restore();
    },
  };

  const lineChartData = getLineChartData();
  const selectedMetricLabel = reportData.chart?.metrics[selectedMetric]?.label ?? t("Total");
  const selectedMetricType = reportData.chart?.metrics[selectedMetric]?.type ?? "number";

  return (
    <Page
      title={t("Hourly Sales Report")}
      primaryAction={primaryAction}
      breadcrumbs={[
        { prefix: true, label: t("secure.facility.reports.pages.rounds_by_customer_type.008"), url: "/admin/report" },
      ]}
    >
      <Card>
        <Card.Section>
          <div className="report-filters-container">
            <DatePickerInput
              label={t("Date Range")}
              months={windowSize.width > TABLET_WIDTH ? 2 : 1}
              position="left"
              showQuickOptions={windowSize.width > MOBILE_WIDTH}
              startingDate={state.dateRangeStart}
              setStartingDate={setDateRangeStart}
              endingDate={state.dateRangeEnd}
              setEndingDate={setDateRangeEnd}
            />
            <DatePickerInput
              label={t("Compare To Date Range")}
              months={windowSize.width > TABLET_WIDTH ? 2 : 1}
              position="left"
              showQuickOptions={windowSize.width > MOBILE_WIDTH}
              startingDate={state.compareDateRangeStart}
              setStartingDate={setCompareDateRangeStart}
              endingDate={state.compareDateRangeEnd}
              setEndingDate={setCompareDateRangeEnd}
            />
            <FilterDropdown
              label={t("secure.facility.reports.pages.detailed_sales_by_department.011")}
              data={state.allVendors}
              dataLabelProperty={"title"}
              checkBoxId={"vendor_ids"}
              dataValueProperty="id"
              onCheckBoxChange={onCheckBoxChange}
              onClear={() => handleRemoveAll("vendor_ids")}
              onAddAll={() => handleAddAllVendors("vendor_ids")}
              leftAligned={windowSize.width <= MOBILE_WIDTH}
            />
            <FilterDropdown
              label={t("Staff")}
              data={state.allUsers}
              dataLabelProperty={"full_name"}
              checkBoxId={"user_ids"}
              dataValueProperty="id"
              onCheckBoxChange={onCheckBoxChange}
              onClear={() => handleRemoveAll("user_ids")}
              onAddAll={() => handleAddAllStaff("user_ids")}
              leftAligned={windowSize.width <= MOBILE_WIDTH}
            />
            <FilterDropdown
              label={t("Registers")}
              data={[{ id: "null", title: t("No Register") }, ...(state.allRegisters ?? [])]}
              dataLabelProperty={"title"}
              checkBoxId={"register_ids"}
              dataValueProperty="id"
              onCheckBoxChange={onCheckBoxChange}
              onClear={() => handleRemoveAll("register_ids")}
              onAddAll={() => handleAddAllRegisters("register_ids")}
              leftAligned={windowSize.width <= MOBILE_WIDTH}
            />
          </div>
        </Card.Section>
      </Card>


      {reportData?.columns?.length > 0 ? (
        <div className="flex flex-col gap-4 pt-4">
          {reportData.chart ? (
            <Card>
              <Card.Section>
                <div className="flex flex-col gap-4">
                  <div className="w-25">
                    <SelectNew
                      id="hourly-sales-metric"
                      label={t("Metric")}
                      options={getMetricOptions()}
                      value={selectedMetric}
                      onChange={value => setSelectedMetric(value)}
                    />
                  </div>
                  <div className="pie-chart-container" style={{ width: "100%" }}>
                    <Line
                      ref={chartRef}
                      data={{
                        labels: lineChartData.labels,
                        datasets: lineChartData.datasets,
                      }}
                      options={{
                        responsive: true,
                        aspectRatio: 2.5,
                        interaction: {
                          mode: "index",
                          intersect: false,
                        },
                        plugins: {
                          legend: {
                            position: "top",
                          },
                          title: {
                            display: true,
                            text: selectedMetricLabel,
                            align: "start",
                            font: {
                              size: 16,
                              weight: "bold",
                            },
                          },
                          tooltip: {
                            callbacks: {
                              label: context => {
                                const value = context.parsed.y;
                                return `${context.dataset.label}: ${formatChartValue(value, selectedMetricType)}`;
                              },
                            },
                          },
                        },
                        scales: {
                          x: {
                            title: {
                              display: true,
                              text: t("Hour"),
                            },
                          },
                          y: {
                            title: {
                              display: true,
                              text: selectedMetricLabel,
                            },
                            ticks: {
                              callback: value => formatChartValue(Number(value), selectedMetricType),
                            },
                          },
                        },
                      }}
                      plugins={[whiteBackgroundPlugin as any]}
                    />
                    <div className="pie-chart-icon">
                      <ButtonNew
                        onClick={() => downloadChart(chartRef)}
                        type="link"
                      >
                        <FontAwesomeIcon icon={faSave} />
                      </ButtonNew>
                    </div>
                  </div>
                </div>
              </Card.Section>
            </Card>
          ) : null}
        </div>
      ) : null}
      <ReportEmailModal
        open={reportEmail.open}
        onEmailSelected={handleReportEmailSelected}
        onClose={handleReportEmailClose}
      />
    </Page>
  );
}
